// Editorial wireframe fallback that ships before Spline hydrates.
// Poster-grade silhouette: thin white line art on pure black with
// two yellow micro-accents. Reads as a still frame from a premium
// piece rather than a cartoon mascot.
import React from "react";

export const KostajaRobot = ({ className = "", testId = "kostaja-robot-static" }) => {
  return (
    <svg
      data-testid={testId}
      className={className}
      viewBox="0 0 400 560"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Kostaja"
      role="img"
    >
      <defs>
        {/* Soft radial ground shadow */}
        <radialGradient id="k-ground" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.10" />
          <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0.02" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
        {/* Chest core glow */}
        <radialGradient id="k-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFC600" stopOpacity="1" />
          <stop offset="70%" stopColor="#FFC600" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#FFC600" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Corner registration marks (editorial poster cue) */}
      <g stroke="#FFFFFF" strokeOpacity="0.28" strokeWidth="1">
        <path d="M8 8 L28 8 M8 8 L8 28" />
        <path d="M392 8 L372 8 M392 8 L392 28" />
        <path d="M8 552 L28 552 M8 552 L8 532" />
        <path d="M392 552 L372 552 M392 552 L392 532" />
      </g>

      {/* Vertical centerline guide */}
      <line
        x1="200"
        y1="42"
        x2="200"
        y2="520"
        stroke="#FFFFFF"
        strokeOpacity="0.06"
        strokeWidth="1"
        strokeDasharray="2 6"
      />

      {/* Ground shadow ellipse */}
      <ellipse cx="200" cy="510" rx="120" ry="10" fill="url(#k-ground)" />

      {/* Antenna: thin line, yellow terminal dot */}
      <line x1="200" y1="34" x2="200" y2="80" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="200" cy="30" r="4" fill="#FFC600" />

      {/* Head: slim outlined block */}
      <rect
        x="152"
        y="82"
        width="96"
        height="98"
        rx="3"
        stroke="#FFFFFF"
        strokeWidth="1.5"
      />
      {/* Head bevel top */}
      <line x1="160" y1="82" x2="240" y2="82" stroke="#FFFFFF" strokeOpacity="0.6" strokeWidth="1" />
      {/* Visor recess (very subtle dark inner rectangle for depth) */}
      <rect x="160" y="102" width="80" height="30" fill="#FFFFFF" fillOpacity="0.04" />
      {/* Visor slit line (yellow accent) */}
      <line x1="164" y1="117" x2="236" y2="117" stroke="#FFC600" strokeWidth="1.5" strokeLinecap="round" />
      {/* Two micro-notches on the visor slit */}
      <rect x="178" y="115" width="4" height="4" fill="#FFC600" />
      <rect x="218" y="115" width="4" height="4" fill="#FFC600" />
      {/* Mouth: single thin line, no cartoon grill */}
      <line x1="176" y1="158" x2="224" y2="158" stroke="#FFFFFF" strokeOpacity="0.5" strokeWidth="1" strokeLinecap="round" />
      {/* Side sensors */}
      <line x1="152" y1="120" x2="146" y2="120" stroke="#FFFFFF" strokeOpacity="0.5" strokeWidth="1.5" />
      <line x1="248" y1="120" x2="254" y2="120" stroke="#FFFFFF" strokeOpacity="0.5" strokeWidth="1.5" />

      {/* Neck: two thin parallel lines */}
      <line x1="188" y1="180" x2="188" y2="204" stroke="#FFFFFF" strokeWidth="1.5" />
      <line x1="212" y1="180" x2="212" y2="204" stroke="#FFFFFF" strokeWidth="1.5" />
      {/* Collar bar */}
      <line x1="182" y1="204" x2="218" y2="204" stroke="#FFFFFF" strokeOpacity="0.4" strokeWidth="1" />

      {/* Torso: tall slim outlined block */}
      <rect
        x="126"
        y="208"
        width="148"
        height="188"
        rx="4"
        stroke="#FFFFFF"
        strokeWidth="1.5"
      />
      {/* Torso inner panel lines: subtle articulation */}
      <line x1="150" y1="230" x2="150" y2="378" stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="1" />
      <line x1="250" y1="230" x2="250" y2="378" stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="1" />
      <line x1="126" y1="318" x2="274" y2="318" stroke="#FFFFFF" strokeOpacity="0.22" strokeWidth="1" />
      <line x1="126" y1="356" x2="274" y2="356" stroke="#FFFFFF" strokeOpacity="0.12" strokeWidth="1" />

      {/* Chest core glow behind the yellow square */}
      <circle cx="200" cy="288" r="26" fill="url(#k-core)" />
      {/* Core square (single yellow micro-accent, main focal point) */}
      <rect
        x="192"
        y="280"
        width="16"
        height="16"
        fill="#FFC600"
      />
      {/* Core hairline crosshair */}
      <line x1="200" y1="272" x2="200" y2="304" stroke="#FFC600" strokeOpacity="0.4" strokeWidth="1" />
      <line x1="184" y1="288" x2="216" y2="288" stroke="#FFC600" strokeOpacity="0.4" strokeWidth="1" />

      {/* Shoulders */}
      <line x1="126" y1="220" x2="106" y2="240" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="274" y1="220" x2="294" y2="240" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="106" cy="242" r="4" stroke="#FFFFFF" strokeWidth="1.5" fill="#000000" />
      <circle cx="294" cy="242" r="4" stroke="#FFFFFF" strokeWidth="1.5" fill="#000000" />

      {/* Arms: slim vertical, joint circle at elbow */}
      <line x1="106" y1="246" x2="106" y2="330" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="106" cy="334" r="4" stroke="#FFFFFF" strokeWidth="1.5" fill="#000000" />
      <line x1="106" y1="338" x2="106" y2="404" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" />
      {/* Hand hint */}
      <line x1="100" y1="406" x2="112" y2="406" stroke="#FFFFFF" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round" />

      <line x1="294" y1="246" x2="294" y2="330" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="294" cy="334" r="4" stroke="#FFFFFF" strokeWidth="1.5" fill="#000000" />
      <line x1="294" y1="338" x2="294" y2="404" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="288" y1="406" x2="300" y2="406" stroke="#FFFFFF" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round" />

      {/* Waist band */}
      <line x1="130" y1="396" x2="270" y2="396" stroke="#FFFFFF" strokeOpacity="0.35" strokeWidth="1" />

      {/* Legs: slim outlined rectangles with knee joint */}
      <rect
        x="160"
        y="400"
        width="30"
        height="92"
        rx="2"
        stroke="#FFFFFF"
        strokeWidth="1.5"
      />
      <rect
        x="210"
        y="400"
        width="30"
        height="92"
        rx="2"
        stroke="#FFFFFF"
        strokeWidth="1.5"
      />
      {/* Knees */}
      <line x1="160" y1="446" x2="190" y2="446" stroke="#FFFFFF" strokeOpacity="0.35" strokeWidth="1" />
      <line x1="210" y1="446" x2="240" y2="446" stroke="#FFFFFF" strokeOpacity="0.35" strokeWidth="1" />

      {/* Feet: slim horizontal bars */}
      <rect x="150" y="492" width="50" height="6" stroke="#FFFFFF" strokeWidth="1.5" />
      <rect x="200" y="492" width="50" height="6" stroke="#FFFFFF" strokeWidth="1.5" />

      {/* Coordinate stamp bottom-right (editorial detail) */}
      <text
        x="368"
        y="546"
        textAnchor="end"
        fontSize="8"
        fontFamily="Poppins, ui-sans-serif, system-ui, sans-serif"
        fontWeight="500"
        letterSpacing="0.18em"
        fill="#FFFFFF"
        fillOpacity="0.28"
      >
        K/01
      </text>
      <text
        x="32"
        y="546"
        fontSize="8"
        fontFamily="Poppins, ui-sans-serif, system-ui, sans-serif"
        fontWeight="500"
        letterSpacing="0.18em"
        fill="#FFFFFF"
        fillOpacity="0.28"
      >
        KOSTAJA
      </text>
    </svg>
  );
};

export default KostajaRobot;
